import { isValidStoragePath, readAllBytes, redactCredentials, type Bridge, type HttpResponse } from '@casa/bridge';
import type { PermissionGate } from '@casa/driver-sdk';
import type { HostCall, HostCallResults, HttpCall, SocketEvent } from '../messages.ts';

/**
 * The page side of a driver's `Host` calls: each one is checked with the permission gate, and only
 * then reaches the bridge. Refusals are logged (without credentials) and answered with the reason.
 * Shared by every host the app runs in, and tested against the Node bridge host.
 */

declare const TextEncoder: new () => { encode(text: string) : Uint8Array };
declare const TextDecoder: new () => { decode(bytes: Uint8Array) : string };
declare const setTimeout: (callback: () => void, ms: number) => unknown;

export const MAX_OPEN_SOCKETS = 16;
export const MAX_STORAGE_VALUE_BYTES = 1024 * 1024;
const STORAGE_KEY_PATTERN = /^[A-Za-z0-9._-]{1,128}$/;

export interface HostCallsOptions {
    bridge: Bridge;
    gate: PermissionGate;
    /** The storage folder of this driver instance, for example `driver-data/<connection>`. */
    storagePrefix: string;
    /** Names the driver instance in logs. */
    label: string;
    log(message: string) : void;
    onSocketEvent(event: SocketEvent) : void;
    /** Every HTTP response's `Date` header and arrival time, for the clock check. */
    onServerDate?: (date: string | undefined, receivedAt: number) => void;
}

export interface CallAnswer {
    value: unknown;
    transfer: ArrayBuffer[];
}

export interface HostCalls {
    /** Rejects with the reason when the gate refuses the call or the bridge fails. */
    handle(call: HostCall) : Promise<CallAnswer>;
    closeAll() : void;
}

interface OpenSocket {
    send(data: string | Uint8Array) : Promise<void>;
    close() : Promise<void>;
}

const answer = <K extends keyof HostCallResults>(value: HostCallResults[K], transfer: ArrayBuffer[] = []) : CallAnswer => ({ value, transfer });

export function createHostCalls(options: HostCallsOptions) : HostCalls {
    const { bridge, gate } = options;
    const sockets = new Map<number, OpenSocket>();
    let nextSocket = 1;
    let closed = false;

    const refuse = (what: string, reason: string) : never => {
        options.log(`${options.label}: refused ${redactCredentials(what)}: ${reason}`);
        throw new Error(reason);
    };

    const storagePath = (key: string) : string => {
        const path = `${options.storagePrefix}/${key}`;
        if (!STORAGE_KEY_PATTERN.test(key) || key === '.' || key === '..' || !isValidStoragePath(path)) {
            throw new Error(`not a valid storage key: ${key}`);
        }

        return path;
    };

    const checkSocketRoom = (what: string) : void => {
        if (sockets.size >= MAX_OPEN_SOCKETS) {
            refuse(what, `too many open connections (at most ${MAX_OPEN_SOCKETS})`);
        }
    };

    /** Registers an opened channel; events go to the Worker until it closes. */
    const adopt = (socket: OpenSocket, onClose: (listener: (reason?: string) => void) => void) : number => {
        const id = nextSocket++;
        sockets.set(id, socket);
        onClose((reason) => {
            if (sockets.delete(id)) {
                options.onSocketEvent({ type: 'socket', socket: id, event: 'close', ...(reason !== undefined ? { reason } : {}) });
            }
        });
        if (closed) {
            void socket.close();
        }

        return id;
    };

    /** Sends a `fetch` or `stream` call through the gate to the bridge; resolves once the head has arrived. */
    const request = async (call: HttpCall, what: string) : Promise<HttpResponse> => {
        const checked = gate.checkHttp(call.url);
        if (!checked.ok) {
            return refuse(`${what} ${call.url}`, checked.reason);
        }

        const response = await bridge.net.http.request({
            method: call.method,
            url: checked.value,
            headers: call.headers,
            ...(call.body ? { body: call.body } : {}),
            ...(call.timeoutMs !== undefined ? { timeoutMs: call.timeoutMs } : {}),
            ...(call.redirect === 'manual' ? { redirect: 'manual' as const } : {}),
            // Checked by the bridge's client and the shell, which refuse what is not well formed.
            ...(call.tls ? { tls: call.tls } : {}),
        });
        options.onServerDate?.(response.headers.date, Date.now());

        return response;
    };

    const handle = async (call: HostCall) : Promise<CallAnswer> => {
        switch (call.op) {
            case 'fetch': {
                const response = await request(call, 'fetch');
                const body = await readAllBytes(response.body);

                return answer<'fetch'>({ status: response.status, headers: response.headers, body }, [body.buffer as ArrayBuffer]);
            }
            case 'stream': {
                checkSocketRoom(`stream ${call.url}`);
                const response = await request(call, 'stream');
                const pieces = response.body[Symbol.asyncIterator]();
                let ended: ((reason?: string) => void) | undefined;
                const socket = adopt(
                    {
                        send: async () => {
                            throw new Error('a stream takes no data');
                        },
                        close: async () => {
                            // Cancels the request at once, also while a piece is awaited (the bridge's body allows it).
                            await pieces.return?.();
                            ended?.();
                        },
                    },
                    (listener) => {
                        ended = listener;
                    },
                );
                // After the reply, which is posted as soon as this resolves: the Worker listens only once it
                // has it. Each bridge frame arrives on its own, so no piece is waiting yet now; this keeps it so.
                setTimeout(() => {
                    void (async () => {
                        try {
                            for (let next = await pieces.next(); !next.done && sockets.has(socket); next = await pieces.next()) {
                                options.onSocketEvent({ type: 'socket', socket, event: 'message', data: next.value });
                            }
                            ended?.();
                        } catch (error) {
                            ended?.(error instanceof Error ? error.message : String(error));
                        }
                    })();
                }, 0);

                return answer<'stream'>({ socket, status: response.status, headers: response.headers });
            }
            case 'ws.open': {
                const checked = gate.checkWs(call.url);
                if (!checked.ok) {
                    return refuse(`ws ${call.url}`, checked.reason);
                }
                checkSocketRoom(`ws ${call.url}`);

                const channel = await bridge.net.ws.open(checked.value, {
                    ...(call.headers ? { headers: call.headers } : {}),
                    ...(call.protocols ? { protocols: call.protocols } : {}),
                });
                const socket = adopt(
                    { send: (data) => channel.send(data), close: () => channel.close() },
                    (listener) => channel.onClose(listener),
                );
                channel.onMessage((data) => {
                    if (sockets.has(socket)) {
                        options.onSocketEvent({ type: 'socket', socket, event: 'message', data });
                    }
                });

                return answer<'ws.open'>({ socket, protocol: channel.protocol });
            }
            case 'tcp.open': {
                const checked = gate.checkTcp(call.host, call.port);
                if (!checked.ok) {
                    return refuse(`tcp ${call.host}:${call.port}`, checked.reason);
                }
                checkSocketRoom(`tcp ${call.host}:${call.port}`);

                const channel = await bridge.net.tcp.open(checked.value, call.port);
                const socket = adopt(
                    {
                        send: async (data) => channel.send(typeof data === 'string' ? new TextEncoder().encode(data) : data),
                        close: () => channel.close(),
                    },
                    (listener) => channel.onClose(listener),
                );
                channel.onData((data) => {
                    if (sockets.has(socket)) {
                        options.onSocketEvent({ type: 'socket', socket, event: 'message', data });
                    }
                });

                return answer<'tcp.open'>({ socket });
            }
            case 'socket.send': {
                const socket = sockets.get(call.socket);
                if (!socket) {
                    throw new Error('the connection is closed');
                }

                await socket.send(call.data);

                return answer<'socket.send'>(null);
            }
            case 'socket.close':
                await sockets.get(call.socket)?.close();

                return answer<'socket.close'>(null);
            case 'storage.get': {
                const data = await bridge.storage.read(storagePath(call.key));

                return answer<'storage.get'>({ value: data ? new TextDecoder().decode(data) : null });
            }
            case 'storage.set': {
                const data = new TextEncoder().encode(call.value);
                if (data.length > MAX_STORAGE_VALUE_BYTES) {
                    throw new Error(`a stored value is at most ${MAX_STORAGE_VALUE_BYTES} bytes`);
                }

                await bridge.storage.write(storagePath(call.key), data);

                return answer<'storage.set'>(null);
            }
            case 'storage.remove':
                await bridge.storage.remove(storagePath(call.key));

                return answer<'storage.remove'>(null);
        }
    };

    return {
        handle,
        closeAll() {
            closed = true;
            for (const socket of sockets.values()) {
                void socket.close().catch(() => {
                    // Already closed or cut off; nothing is waiting for it.
                });
            }
            sockets.clear();
        },
    };
}
